'use client';

import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { CFDE_DEV_AUTH, CFDE_DEV_OWNER } from '@/integrations/next/auth/devAuth';
import { agentChatBackend } from './agentChatBackend';
import { createAgentChatController } from './agentChatController';
import { MicrophoneTestProvider } from './MicrophoneTestProvider';
import { createVoiceInputController } from './voiceInputController';
import { createMicrophoneTestController } from './microphoneTestController';
import { createSpeechPlaybackController } from './speechPlaybackController';
import { createForgeSpeechPlayer } from './forgeSpeechPlayer';
import { narrationSilence } from '@/narrationSilence';
import { enableVoiceDiagnostics, voiceDiagnostic, diagnosticErrorType } from './voiceDiagnostics';
import { createTranscriptionTestController } from './transcriptionTestController';

/** Episode narration is silent while the viewer talks to the agent or hears its reply. */
const NARRATION_SILENCE_REASON = 'agent';

type Controller = ReturnType<typeof createAgentChatController>;
const ChatContext = createContext<{ controller: Controller; voice: ReturnType<typeof createVoiceInputController>;
    speech: ReturnType<typeof createSpeechPlaybackController>;
    transcriptionTest: ReturnType<typeof createTranscriptionTestController>; authenticated: boolean } | null>(null);

export function AgentChatProvider({ children }: { children: ReactNode }) {
    const { data: session, status } = useSession();
    const owner = CFDE_DEV_AUTH ? CFDE_DEV_OWNER : status === 'authenticated' ? session?.user?.email : undefined;
    const { controller, microphone } = useMemo(() => {
        const key = `tp_agent_thread:${owner ?? 'signed-out'}`;
        return { controller: createAgentChatController(agentChatBackend, () => window.localStorage, key),
            microphone: createMicrophoneTestController() };
    }, [owner]);
    const voice = useMemo(() => createVoiceInputController(controller), [controller]);
    const transcriptionTest = useMemo(() => createTranscriptionTestController(controller), [controller]);
    const speech = useMemo(() => createSpeechPlaybackController(controller, microphone, undefined, undefined, createForgeSpeechPlayer()), [controller, microphone]);
    useEffect(() => {
        if (!owner) return;
        let disable: (() => void) | undefined;
        const offVoice = voice.subscribe(() => {
            const snapshot = voice.getSnapshot();
            if (snapshot.enabled && !disable) disable = enableVoiceDiagnostics();
        });
        let previous = '';
        const offChat = controller.subscribe(() => {
            const snapshot = controller.getSnapshot();
            const failed = !!(snapshot.error || snapshot.view?.error);
            const next = `${snapshot.sending}:${failed}`;
            if (next !== previous) {
                previous = next;
                voiceDiagnostic('chat-state', { sending: snapshot.sending, failed });
            }
        });
        const visibility = () => voiceDiagnostic('page-visibility', { phase: document.visibilityState === 'hidden' ? 'hidden' : 'visible' });
        const pagehide = (event: PageTransitionEvent) => voiceDiagnostic('page-hide', { persisted: event.persisted });
        const pageshow = (event: PageTransitionEvent) => voiceDiagnostic('page-show', { persisted: event.persisted });
        const unload = () => voiceDiagnostic('page-unload');
        const resourceError = (event: Event) => {
            const target = event.target;
            const url = target instanceof HTMLScriptElement ? target.src
                : target instanceof HTMLLinkElement ? target.href : undefined;
            if (url && new URL(url, location.href).pathname.startsWith('/_next/')) {
                voiceDiagnostic('next-resource-error');
            }
        };
        const error = (event: ErrorEvent) => voiceDiagnostic('browser-error', { errorType: diagnosticErrorType(event.error) });
        const rejection = (event: PromiseRejectionEvent) => voiceDiagnostic('browser-rejection', { errorType: diagnosticErrorType(event.reason) });
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('pagehide', pagehide);
        window.addEventListener('pageshow', pageshow);
        window.addEventListener('beforeunload', unload);
        window.addEventListener('error', resourceError, true);
        window.addEventListener('error', error);
        window.addEventListener('unhandledrejection', rejection);
        return () => {
            offVoice(); offChat();
            document.removeEventListener('visibilitychange', visibility);
            window.removeEventListener('pagehide', pagehide);
            window.removeEventListener('pageshow', pageshow);
            window.removeEventListener('beforeunload', unload);
            window.removeEventListener('error', resourceError, true);
            window.removeEventListener('error', error);
            window.removeEventListener('unhandledrejection', rejection);
            disable?.();
        };
    }, [owner, controller, voice]);
    useEffect(() => {
        if (!owner) return;
        const stop = speech.start();
        window.addEventListener('pagehide', speech.stop);
        return () => { window.removeEventListener('pagehide', speech.stop); stop(); };
    }, [speech, owner]);
    useEffect(() => { if (owner) return controller.start(); }, [controller, owner]);
    useEffect(() => {
        if (!owner) return;
        const stop = transcriptionTest.start();
        const hide = () => { transcriptionTest.select(false); };
        window.addEventListener('pagehide', hide);
        return () => { window.removeEventListener('pagehide', hide); stop(); };
    }, [owner, transcriptionTest]);
    useEffect(() => {
        if (!owner) return;
        const stop = voice.start();
        const pagehide = () => voice.cancel();
        window.addEventListener('pagehide', pagehide);
        return () => { window.removeEventListener('pagehide', pagehide); stop(); };
    }, [voice, owner]);
    useEffect(() => {
        const update = () => narrationSilence.set(NARRATION_SILENCE_REASON,
            ['requesting', 'recording', 'stopping'].includes(voice.getSnapshot().phase)
            || ['requesting', 'recording', 'finishing'].includes(transcriptionTest.getSnapshot().phase)
            || speech.getSnapshot().phase === 'speaking');
        const offVoice = voice.subscribe(update);
        const offSpeech = speech.subscribe(update);
        const offTest = transcriptionTest.subscribe(update);
        update();
        return () => { offVoice(); offSpeech(); offTest(); narrationSilence.set(NARRATION_SILENCE_REASON, false); };
    }, [voice, speech, transcriptionTest]);
    const value = useMemo(() => ({ controller, voice, speech, transcriptionTest, authenticated: !!owner }), [controller, voice, speech, transcriptionTest, owner]);
    return <ChatContext.Provider value={value}>
        <MicrophoneTestProvider key={owner ?? 'signed-out'} controller={microphone}>{children}</MicrophoneTestProvider>
    </ChatContext.Provider>;
}

export function useAgentChat() {
    const context = useContext(ChatContext);
    if (!context) throw new Error('Agent chat requires AgentChatProvider.');
    const state = useSyncExternalStore(context.controller.subscribe, context.controller.getSnapshot, context.controller.getSnapshot);
    return { ...context, state };
}

export function useVoiceInput() {
    const { voice } = useAgentChat();
    const state = useSyncExternalStore(voice.subscribe, voice.getSnapshot, voice.getSnapshot);
    return { controller: voice, state };
}

export function useSpeechPlayback() {
    const { speech } = useAgentChat();
    const state = useSyncExternalStore(speech.subscribe, speech.getSnapshot, speech.getSnapshot);
    return { controller: speech, state };
}

export function useTranscriptionTest() {
    const { transcriptionTest } = useAgentChat();
    const state = useSyncExternalStore(transcriptionTest.subscribe, transcriptionTest.getSnapshot, transcriptionTest.getSnapshot);
    return { controller: transcriptionTest, state };
}
